'use client';
import React from 'react';
import CartLineItem from '@repo/ui/src/components/Cart/CartLineItem';
import CartOrderSummary from '@repo/ui/src/components/Cart/CartOrderSummary';
import type {
  CartLineItem as CartLineItemType,
  CartTotals,
  CartSummaryExtra,
} from '@repo/ui/src/types/cart';

interface CartBodyProps {
  lineItems: Array<CartLineItemType>;
  isLoading: boolean;
  isEmpty: boolean;
  outOfStockIds?: Array<string>;
  /** id of the item currently being updated (shows loading state on that item only). */
  updatingItemId?: string;
  onQuantityChange: (id: string, newQuantity: number) => void;
  onRemove: (id: string) => void;
  showSummary?: boolean;
  totals?: CartTotals;
  extraSummaryLines?: Array<CartSummaryExtra>;
  /** Slot for app-specific extra items rendered below line items (e.g. Traeger gift cards). */
  additionalItemsSlot?: React.ReactNode;
  /** Custom loading indicator. */
  loadingNode?: React.ReactNode;
  promotionalNode?: React.ReactNode;
  emptyMessage?: string;
  // i18n forwarded to CartLineItem
  outOfStockMessage?: string;
  removeLabel?: string;
  outOfStockLabel?: string;
  quantityLabel?: string;
  // i18n forwarded to CartOrderSummary
  subtotalLabel?: string;
  shippingLabel?: string;
  shippingFreeLabel?: string;
  taxLabel?: string;
  taxFreeLabel?: string;
  currency?: string;
  isCartPage?: boolean;
}

const CartBody = ({
  lineItems,
  isLoading,
  isEmpty,
  outOfStockIds,
  updatingItemId,
  onQuantityChange,
  onRemove,
  showSummary = true,
  totals,
  extraSummaryLines,
  additionalItemsSlot,
  loadingNode,
  promotionalNode,
  emptyMessage = 'Your cart is currently empty.',
  outOfStockMessage,
  removeLabel,
  outOfStockLabel,
  quantityLabel,
  subtotalLabel,
  shippingLabel,
  shippingFreeLabel,
  taxLabel,
  taxFreeLabel,
  currency,
  isCartPage,
}: CartBodyProps) => {
  return (
    <div suppressHydrationWarning>
      {isLoading ? (
        (loadingNode ?? null)
      ) : isEmpty ? (
        <div className="h-15 w-full bg-warm-200 px-4 flex justify-center items-center mt-8">
          <p className="tra-text-web-body-xl mb-0">{emptyMessage}</p>
        </div>
      ) : (
        <>
          <ul className="list-none p-0">
            {lineItems.map((item) => (
              <CartLineItem
                key={item.id}
                item={item}
                outOfStock={outOfStockIds?.includes(item.id)}
                isUpdating={updatingItemId === item.id}
                onQuantityChange={onQuantityChange}
                onRemove={onRemove}
                outOfStockMessage={outOfStockMessage}
                removeLabel={removeLabel}
                outOfStockLabel={outOfStockLabel}
                quantityLabel={quantityLabel}
                currency={currency}
              />
            ))}
            {additionalItemsSlot}
          </ul>
          {showSummary && totals && (
            <div className="pt-8 mb-8">
              <CartOrderSummary
                totals={totals}
                extraLines={extraSummaryLines}
                promotionalNode={promotionalNode}
                subtotalLabel={subtotalLabel}
                shippingLabel={shippingLabel}
                shippingFreeLabel={shippingFreeLabel}
                taxLabel={taxLabel}
                taxFreeLabel={taxFreeLabel}
                isCartPage={isCartPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CartBody;
